import { niceStep } from "./profit-chart";
import { shortMoney } from "./format";
import { useChartWidth } from "./use-chart-width";
import styles from "./accounting.module.css";

/** The widest a column is drawn, in pixels. */
const MAX_BAR_WIDTH = 36;

/** One column of a stacked chart: its segments bottom to top, and what it says. */
export interface StackedBar {
  id: string;
  /** Under the column. */
  label: string;
  /** Every segment is zero or more, in the order of `segmentClasses`. */
  segments: readonly number[];
  /** What a hover and a screen reader say — the exact figures. */
  title: string;
}

/**
 * Money out by group, stacked by month (STORY-182b, AF-D4, AF-D8).
 *
 * Hand-drawn SVG like `ProfitChart`, for the same reason: one shape, and a
 * dependency would be more code than the chart. **One scale for the columns,
 * the gridlines and the axis labels**, so a column cannot rise above the line
 * that names its height. Segments are separated by a thin surface-coloured gap
 * and named in a legend and in the title of every column, so no group is told
 * apart by colour alone.
 *
 * Every column is a button: Enter or Space picks it.
 */
export function StackedChart({
  bars,
  segmentClasses,
  currency,
  label,
  onPick,
  height = 170,
  testId,
}: {
  bars: readonly StackedBar[];
  /** One class per segment, in the order `segments` are given. */
  segmentClasses: readonly (string | undefined)[];
  currency: string;
  label: string;
  onPick?: (id: string) => void;
  height?: number;
  testId?: string;
}): React.JSX.Element {
  const [box, width] = useChartWidth();
  const padLeft = 64;
  const padRight = 8;
  const padTop = 12;
  const padBottom = 26;

  const totals = bars.map((bar) => bar.segments.reduce((sum, value) => sum + value, 0));
  const max = Math.max(0, ...totals) || 100;
  const step = niceStep(max, 4);
  const high = Math.ceil(max / step) * step;
  const y = (value: number): number =>
    padTop + ((high - value) / high) * (height - padTop - padBottom);

  const ticks: number[] = [];
  for (let tick = 0; tick <= high + step / 2; tick += step) ticks.push(tick);

  const slot = (width - padLeft - padRight) / Math.max(1, bars.length);
  const barWidth = Math.min(MAX_BAR_WIDTH, Math.max(3, slot * 0.6));

  return (
    <div ref={box}>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        role="group"
        aria-label={label}
        data-testid={testId}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className={tick === 0 ? styles.zeroLine : styles.gridLine}
              x1={padLeft}
              x2={width - padRight}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              className={styles.axisText}
              x={padLeft - 8}
              y={y(tick) + 4}
              fontSize={11}
              textAnchor="end"
            >
              {shortMoney(tick, currency)}
            </text>
          </g>
        ))}

        {bars.map((bar, i) => {
          const x = padLeft + i * slot;
          const barX = x + (slot - barWidth) / 2;
          const pick = (): void => onPick?.(bar.id);
          let below = 0;
          return (
            <g
              key={bar.id}
              className={styles.bar}
              role="button"
              tabIndex={0}
              aria-label={bar.title}
              onClick={pick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  pick();
                }
              }}
              data-testid={testId === undefined ? undefined : `${testId}-bar`}
            >
              <title>{bar.title}</title>
              <rect
                className={styles.barHit}
                x={x + 1}
                y={padTop}
                width={Math.max(1, slot - 2)}
                height={height - padTop - padBottom}
                rx={4}
              />
              {bar.segments.map((value, index) => {
                if (value <= 0) return null;
                const top = y(below + value);
                const tall = y(below) - top;
                below += value;
                return (
                  <rect
                    key={index}
                    className={`${segmentClasses[index] ?? ""} ${styles.stackSegment}`}
                    x={barX}
                    y={top}
                    width={barWidth}
                    height={Math.max(1, tall)}
                  />
                );
              })}
              <text
                className={styles.barLabel}
                x={x + slot / 2}
                y={height - 8}
                fontSize={12}
                textAnchor="middle"
              >
                {bar.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
